// Per-route <title>, meta description and canonical for a single-page app.
//
// The problem this exists to solve: index.html carries one hardcoded
// `<link rel="canonical" href="https://reanloforreaders.com/">`, and because one
// HTML file serves every route, every page was telling search engines "the real
// version of me is the homepage — index that instead". A canonical is an
// instruction, not a hint, so nothing on the site could rank on its own.
//
// Deliberately dependency-free. react-helmet-async would need a provider
// wrapped around the whole app, and a bug there takes the entire site down.
// This only ever writes to document.head after render, so the worst it can do
// is nothing at all. Every DOM call is wrapped for the same reason.

const SITE = 'https://reanloforreaders.com';

// Routes we want search engines to keep, with the copy to use.
// A route absent from here still gets a correct canonical — it just keeps the
// site-wide title and description from index.html.
export const ROUTE_SEO = {
  '/':              { title: 'Reanlo | Conversation-Powered Book Discovery',
                      description: "A conversational book matchmaker. Describe the book you're craving in your own words, and Reanlo bridges the gap between incredible books and the readers searching for them." },
  '/discover':      { title: 'Reading Beyond the Algorithm | Reanlo',
                      description: "Conversation-powered book discovery. Say what you're craving, the way you'd tell a friend, and find the book that fits." },
  '/community':     { title: 'Reader Community | Reanlo',
                      description: 'Talk about what you are reading with people who read the same way you do. Thoughts, hot takes and recommendations from real readers.' },
  '/reader-corner': { title: "Reader's Corner | Reanlo",
                      description: 'Longer reads from the Reanlo community: newsletters, essays and recommendations written by readers.' },
  '/terms':         { title: 'Terms of Service | Reanlo', description: 'The terms that apply when you use Reanlo.' },
  '/privacy':       { title: 'Privacy Policy | Reanlo',   description: 'What Reanlo collects, why, and what we never do with it.' },
  '/how-matching-works': { title: 'How Matching Works | Reanlo',
                      description: 'No generative AI. Reanlo matches the meaning of what an author wrote to the meaning of what a reader is craving -- human words, matched to human words.' },
};

// Pages that must never be indexed. Auth screens have no search value and
// dilute the site; /profile and /settings are a signed-in reader's own pages;
// /u/ profiles carry contact details and are excluded on privacy grounds, not
// SEO ones. /staging is the pre-launch copy of the homepage and would compete
// with the real one.
const NOINDEX_PREFIXES = [
  '/login', '/signup', '/forgot-password', '/reset-password', '/verify-email', '/auth',
  '/profile', '/settings', '/admin', '/u/', '/staging',
];

export const isNoIndex = pathname =>
  NOINDEX_PREFIXES.some(p => pathname === p || pathname.startsWith(p.endsWith('/') ? p : p + '/') || pathname === p.replace(/\/$/, ''));

// Canonical addresses carry no query string. Otherwise ?tab=tropes and
// ?ref=twitter each look like a separate page holding the same content.
export function canonicalFor(pathname) {
  const clean = pathname !== '/' && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return SITE + (clean || '/');
}

function setMeta(selector, attr, value) {
  try {
    let el = document.head.querySelector(selector);
    if (!el) {
      el = document.createElement(selector.startsWith('link') ? 'link' : 'meta');
      const m = selector.match(/\[(name|property|rel)="([^"]+)"\]/);
      if (m) el.setAttribute(m[1], m[2]);
      document.head.appendChild(el);
    }
    el.setAttribute(attr, value);
  } catch { /* head is not writable — leave the document as it was */ }
}

// The site-wide title and description index.html shipped with, captured before
// anything is overwritten. A route with no entry of its own falls back to these
// rather than keeping whatever the previous route set — without this, walking
// from /community to a profile leaves the page titled "Reader Community".
const DEFAULTS = (() => {
  try {
    return {
      title: document.title,
      description: document.head.querySelector('meta[name="description"]')?.getAttribute('content') || '',
    };
  } catch {
    return { title: '', description: '' };
  }
})();

// Called once, from the router. Every route gets the right canonical without
// any page component having to know this exists.
export function applyRouteSeo(pathname) {
  try {
    const seo = ROUTE_SEO[pathname] || DEFAULTS;

    if (seo.title) document.title = seo.title;
    if (seo.description) {
      setMeta('meta[name="description"]', 'content', seo.description);
      setMeta('meta[property="og:description"]', 'content', seo.description);
    }
    if (seo.title) setMeta('meta[property="og:title"]', 'content', seo.title);

    const url = canonicalFor(pathname);
    setMeta('link[rel="canonical"]', 'href', url);
    setMeta('meta[property="og:url"]', 'content', url);

    setMeta('meta[name="robots"]', 'content',
      isNoIndex(pathname) ? 'noindex, nofollow' : 'index, follow');
  } catch { /* never let metadata break a page */ }
}
